import { useCallback, useMemo, useState } from 'react';
import {
    MenuToggle,
    Select,
    SelectList,
    SelectOption,
    Divider
} from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import './ProfileButton.css';

import { appConfigSelectors } from '../store/appConfig/appConfigSlice';
import { logOutRequested } from '../store/auth/authSlice';
import { DashboardIcon, LogoutIcon, WorkspaceLogo } from '../img';
import { backendTierAppAuths } from '../web-apis/cloud-backend-auth';
import { DeploymentMode } from '../store/appConfig/appDeploymentInfoTypes';
import TooltipWrapper from './tooltip-wrapper';
import ExternalLink from './external-link';

const getInitials = (name: string, isLocalUser: boolean) => {
    if (isLocalUser) {
        return 'L';
    }

    if (!name || typeof name !== 'string') {
        return ''; // Return an empty string if name is invalid
    }
    const parts = name.trim().split(' ');
    const firstInitial = parts[0]?.[0] || ''; // Safely access the first character
    const secondInitial = parts[1]?.[0] || ''; // Safely access the second character, if available
    return (firstInitial + secondInitial).toUpperCase();
};

export const ProfileButton = () => {

    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useDispatch();
    const appDeploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);

    const isLocalMode = !!(appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local));

    const loggedInUsernames = Object.values(backendTierAppAuths)
        .filter(appAuth => appAuth.isLoggedIn)
        .map(appAuth => ({ appName: appAuth.appName, user: appAuth.loggedInUser }));

    // Determine display name and email based on mode
    const displayName = isLocalMode ? t('userProfile.localUser') : loggedInUsernames[0]?.user?.fullName || '';
    const displayEmail = isLocalMode ? '' : loggedInUsernames[0]?.user?.email || '';


    const onLogOutClick = useCallback(() => {
        dispatch(logOutRequested());
    }, [dispatch]);

    const appLinks = useMemo(() => {
        const appLinks: { url: string, label: React.ReactNode, icon: React.ReactNode }[] = [];

        if (appDeploymentInfo?.dashboardUrl) {
            appLinks.push({ url: appDeploymentInfo.dashboardUrl, label: t('userProfile.link.dashboard'), icon: <img src={DashboardIcon} alt={t('userProfile.link.dashboard')} className="profile-icon" /> });
        }

        if (appDeploymentInfo?.workspaceUrl) {
            appLinks.push({ url: appDeploymentInfo.workspaceUrl, label: t('userProfile.link.workspaceWeb'), icon: <img src={WorkspaceLogo} alt={t('userProfile.link.workspaceWeb')} className="profile-icon" /> });
        }

        return appLinks;
    }, [appDeploymentInfo?.dashboardUrl, appDeploymentInfo?.workspaceUrl, t]);

    // Determine if upper profile menu section should be displayed
    const logoutOptionCount = isLocalMode ? 0 : 1; // Show logout option only if not in local mode
    const isUpperProfileMenuDisplayed = (appLinks.length + logoutOptionCount) > 0;

    return (
        <Select
            isOpen={isOpen}
            toggle={(toggleRef) => (
                <MenuToggle
                    ref={toggleRef}
                    isExpanded={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    className="profile-menu-toggle"
                    style={{
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        background: '#00b3c6',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0px 0px 5px 15px'
                    }}
                    icon={<span>{getInitials(displayName, isLocalMode)}</span>}
                    aria-label={t('userProfile.ariaLabel')}
                    data-testid="profile-button"
                />
            )}
            onOpenChange={setIsOpen}
            className="profile-menu-custom"
            popperProps={{ position: 'end' }}
        >
            {isUpperProfileMenuDisplayed && (
                <>
                    <SelectList>
                        {appLinks.map(link => (
                            <SelectOption
                                key={`${link.label}-${link.url}`}
                                value={link.label}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <span style={{ marginRight: 12 }}>{link.icon}</span>
                                    <span><ExternalLink url={link.url} showIcon={false} noStyles={true}>{link.label}</ExternalLink></span>
                                </span>
                            </SelectOption>
                        ))}
                        {!isLocalMode && (
                            <SelectOption
                                value="logout"
                                onClick={onLogOutClick}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <span style={{ marginRight: 12 }}><img src={LogoutIcon} alt={t('common.logOut')} className="profile-icon" /></span>
                                    <span>{t('common.logOut')}</span>
                                </span>
                            </SelectOption>
                        )}
                    </SelectList>
                    <Divider style={{ margin: '0' }} />
                </>
            )}
            <SelectList>
                <TooltipWrapper key="user-info-wrapper" tooltipContent={isLocalMode ? undefined : `${displayName} (${displayEmail})`}>
                    <SelectOption isDisabled value="user-info" className='selectable'>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            {/* Avatar initials circle */}
                            <div className="profile-menu-initials">
                                {getInitials(displayName, isLocalMode)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div className="user-name" style={{ fontWeight: 600, fontSize: 12, color: '#757c85' }}>{displayName}</div>
                                {displayEmail && <div className="user-email" style={{ fontSize: 11, color: '#757c85' }}>{displayEmail}</div>}
                            </div>
                        </div>
                    </SelectOption>
                </TooltipWrapper>
            </SelectList>
        </Select>
    );
};
